import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from './LegalLayout.jsx';

const Mail = () => (
  <a href="mailto:ceo@civixcrm.ai">ceo@civixcrm.ai</a>
);

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy — CIVIX">
      <h1>Privacy Policy</h1>
      <p className="legal-meta">
        <strong>Effective date:</strong> May 13, 2026<br />
        <strong>Last updated:</strong> May 13, 2026
      </p>

      <p>
        CIVIX LLC ("CIVIX," "we," "our," or "us") respects your privacy. This Privacy Policy explains
        how we collect, use, disclose, and safeguard your information when you use the CIVIX customer
        relationship management platform available at civixcrm.ai and any related applications
        (collectively, the "Service").
      </p>
      <p>
        By using the Service, you agree to the collection and use of information in accordance with
        this Policy. If you do not agree with our policies and practices, do not use the Service.
      </p>

      <h2>1. Who we are</h2>
      <p>
        CIVIX LLC is a Colorado limited liability company headquartered at 5600 S Quebec St Suite 300,
        Greenwood Village, CO 80111. CIVIX provides a multi-tenant CRM, dialer, and lead-management
        platform used by independent insurance agents and agencies in the United States.
      </p>

      <h2>2. Information we collect</h2>
      <p>We collect three categories of information:</p>
      <p>
        <strong>a) Information you provide directly.</strong> When you register for an account, we
        collect your name, email address, phone number, mailing address, business name, state insurance
        license number, payment information (processed by Stripe), and authentication credentials.
      </p>
      <p>
        <strong>b) Information about your contacts and leads.</strong> When you upload, import, or
        capture leads through the Service (including via CSV upload, Meta Lead Ads integration, Google
        Ads integration, or third-party lead vendors), we store the lead's name, contact details
        (phone, email, address), state of residence, lead source, consent records, do-not-call status,
        communication history, and other fields you provide.
      </p>
      <p>
        <strong>c) Information collected automatically.</strong> When you use the Service, we collect
        log data, IP address, browser type, device identifiers, pages viewed, timestamps, and similar
        diagnostic information. We use first-party cookies and session storage for authentication and
        to maintain your preferences.
      </p>
      <p>
        We do <strong>not</strong> sell personal information. We do <strong>not</strong> use the
        Service to track you across unrelated third-party websites.
      </p>

      <h2>3. How we use information</h2>
      <p>We use information to:</p>
      <ul>
        <li>Provide, operate, and maintain the Service</li>
        <li>Authenticate users and manage tenant isolation</li>
        <li>Process payments and manage subscriptions through Stripe</li>
        <li>Send transactional communications (login notices, billing, security alerts)</li>
        <li>
          Send SMS and place outbound calls <strong>on behalf of you, our customer</strong>, to leads
          in your tenant — only after you have confirmed lawful consent and only within applicable
          TCPA quiet hours
        </li>
        <li>Detect, prevent, and respond to fraud, abuse, security incidents, and violations of our Terms of Service</li>
        <li>Comply with legal obligations, including regulatory inquiries from the FCC, state attorneys general, and insurance regulators</li>
        <li>Improve and analyze the Service</li>
      </ul>

      <h2>4. SMS and voice communications</h2>
      <p>
        CIVIX enables our customers (licensed insurance agents and agencies) to send SMS messages and
        place outbound calls to leads they have uploaded or captured. CIVIX is the platform provider;
        the customer is the message sender and is solely responsible for obtaining lawful prior
        express written consent under the Telephone Consumer Protection Act (TCPA) and applicable
        state law before any contact occurs.
      </p>
      <p>CIVIX enforces the following controls on every SMS and call dispatched through the Service:</p>
      <ul>
        <li>Per-lead TCPA consent gate (<code>canContact()</code>) — no message or call leaves the platform unless the lead record has a documented consent timestamp</li>
        <li>Quiet hours enforced in the lead's local time zone (no contact 9:00 PM – 8:00 AM local)</li>
        <li>Do-not-call (DNC) check against the Federal DNC Registry and internal DNC list</li>
        <li>STOP / END / QUIT / CANCEL / UNSUBSCRIBE / OPTOUT / REVOKE keyword handling — these immediately and permanently suppress all messages and calls from the platform to that number until the recipient texts START</li>
        <li>State insurance licensing check — messages and calls are blocked if the sending agent does not hold an active license in the lead's state</li>
      </ul>
      <p>
        If you receive an SMS from a phone number associated with CIVIX and want to opt out, reply{' '}
        <strong>STOP</strong>. If you want help, reply <strong>HELP</strong>. Standard message and
        data rates may apply. Message frequency varies. For more, see our{' '}
        <Link to="/sms-policy">SMS Policy</Link>.
      </p>
      <p>
        Mobile opt-in data and consent records are not shared with third parties or affiliates for
        marketing purposes.
      </p>

      <h2>5. How we share information</h2>
      <p>We share information only as follows:</p>
      <ul>
        <li>
          <strong>Service providers.</strong> We use third-party processors to operate the Service:
          Stripe (payments), Twilio (SMS and voice), Render (hosting), MongoDB Atlas (database),
          Cloudflare (DNS / CDN / security), Sentry (error monitoring), and Meta and Google
          (advertising integrations you authorize). Each processor is contractually bound to use
          information only to perform services for CIVIX.
        </li>
        <li>
          <strong>Other tenants.</strong> We do <strong>not</strong> share your information, your
          leads, or your communications with other CIVIX tenants. Multi-tenant isolation is enforced
          at every database query.
        </li>
        <li>
          <strong>Legal compliance.</strong> We may disclose information when required by law,
          subpoena, or court order, or to protect the rights, property, or safety of CIVIX, our users,
          or others.
        </li>
        <li>
          <strong>Business transfers.</strong> If CIVIX is acquired or merges with another company,
          your information may transfer as part of that transaction; you will be notified.
        </li>
      </ul>

      <h2>6. Data retention</h2>
      <p>
        We retain account information for as long as your account is active and for up to 24 months
        after closure for tax, audit, and regulatory purposes. Lead and communication records are
        retained according to your tenant's data-retention setting, with a default of 36 months. You
        may request deletion at any time by emailing <Mail />; we will process the request within 30
        days, subject to legal-hold and TCPA recordkeeping obligations.
      </p>

      <h2>7. Security</h2>
      <p>
        We employ technical and organizational measures appropriate to the sensitivity of the data,
        including TLS encryption in transit, encryption at rest for credentials and payment data,
        role-based access control, multi-tenant isolation at the database query layer, audit logging
        of administrative actions, and least-privilege secrets management. No system is perfectly
        secure; we encourage you to use a strong unique password and enable any multi-factor options
        offered.
      </p>

      <h2>8. Your rights</h2>
      <p>
        If you are a U.S. resident, you may have rights under your state's privacy laws (including
        CCPA/CPRA for California residents and CPA for Colorado residents) to access, correct,
        delete, or port your personal information, and to opt out of certain uses. To exercise these
        rights, email <Mail /> with the subject "Privacy Request" and the email address associated
        with your account. We will verify your identity and respond within the timeframe required by
        applicable law.
      </p>

      <h2>9. Children</h2>
      <p>
        The Service is not directed to individuals under 18 and we do not knowingly collect
        information from anyone under 18. If you believe a child has provided information, contact{' '}
        <Mail /> and we will delete it.
      </p>

      <h2>10. International users</h2>
      <p>
        The Service is designed for use in the United States. If you access the Service from outside
        the U.S., you do so at your own initiative and consent to the transfer of your information to
        the U.S.
      </p>

      <h2>11. Changes to this Policy</h2>
      <p>
        We may update this Policy from time to time. We will post the updated Policy here with a new
        "Last updated" date. Material changes will be communicated via email or in-app notice at
        least 14 days before they take effect.
      </p>

      <h2>12. Contact us</h2>
      <p>
        CIVIX LLC<br />
        5600 S Quebec St Suite 300<br />
        Greenwood Village, CO 80111<br />
        Email: <Mail /><br />
        Phone: +1 (719) 387-0657
      </p>
    </LegalLayout>
  );
}
